import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { Action, Selector, State, StateContext } from '@ngxs/store';
import { finalize, tap } from 'rxjs';

import { GetBlogsAction, GetBlogBySlugAction, GetRecentBlogAction } from '../action/blog.action';
import { Global } from '../data/global';
import { IBlog as ICmsBlog } from '../interface/cms.interface';
import { BlogService } from '../services/blog.service';
import { CmsService } from '../services/cms.service';

export class BlogStateModel {
  blog = {
    data: [] as ICmsBlog[],
    total: 0,
  };
  selectedBlog: ICmsBlog | null;
  recentBlog: ICmsBlog[] | [];
}

@State<BlogStateModel>({
  name: 'blog',
  defaults: {
    blog: {
      data: [],
      total: 0,
    },
    selectedBlog: null,
    recentBlog: [],
  },
})
@Injectable()
export class BlogState {
  private router = inject(Router);
  private blogService = inject(BlogService);
  private cmsService = inject(CmsService);

  @Selector()
  static blog(state: BlogStateModel) {
    return state.blog;
  }

  @Selector()
  static selectedBlog(state: BlogStateModel) {
    return state.selectedBlog;
  }

  @Selector()
  static resentBlog(state: BlogStateModel) {
    return state.recentBlog;
  }

  /** Blog list from cms-api */
  @Action(GetBlogsAction)
  getBlogs(ctx: StateContext<BlogStateModel>) {
    this.blogService.skeletonLoader = true;
    return this.cmsService.getAllBlog(Global.LanguageID).pipe(
      tap({
        next: result => {
          const data: ICmsBlog[] = result?.data || [];
          ctx.patchState({
            blog: {
              data,
              total: data.length,
            },
          });
        },
        error: err => {
          throw new Error(err?.error?.message);
        },
      }),
      finalize(() => (this.blogService.skeletonLoader = false)),
    );
  }

  /** Blog details (with all its content blocks) from cms-api; the route "slug" is the blog's masterId */
  @Action(GetBlogBySlugAction)
  getBlogBySlug(ctx: StateContext<BlogStateModel>, { slug }: GetBlogBySlugAction) {
    ctx.patchState({ selectedBlog: null });

    // Unknown slug (the API answers 500): show the 404 page on the same URL, which also sends a 404 status in SSR
    const notFound = () => void this.router.navigate(['/404'], { skipLocationChange: true });

    return this.cmsService.getBlogDetailsBySlug(slug).pipe(
      tap({
        next: result => {
          if (result?.success && result.data) {
            ctx.patchState({ selectedBlog: result.data });
          } else {
            notFound();
          }
        },
        error: notFound,
      }),
    );
  }

  /** Latest blogs from cms-api for the sidebar */
  @Action(GetRecentBlogAction)
  getRecentBlogs(ctx: StateContext<BlogStateModel>, action: GetRecentBlogAction) {
    const limit = Number(action.payload?.['paginate']) || 5;
    return this.cmsService.getAllBlog(Global.LanguageID).pipe(
      tap({
        next: result => {
          ctx.patchState({
            recentBlog: (result?.data || []).slice(0, limit),
          });
        },
        error: err => {
          throw new Error(err?.error?.message);
        },
      }),
    );
  }
}
